<script lang="ts">
  import * as Dialog from '$lib/components/ui/dialog/index.js'
  import { Button } from '$lib/components/ui/button/index.js'
  import { Input } from '$lib/components/ui/input/index.js'
  import { Label } from '$lib/components/ui/label/index.js'
  import { claimServer } from '$lib/apis/server/api'
  import AlertCircleIcon from '@lucide/svelte/icons/alert-circle'

  let {
    open = $bindable(false),
    onClaimed,
  }: {
    open?: boolean
    onClaimed?: () => void | Promise<void>
  } = $props()

  let serverName = $state('')
  let claimToken = $state('')
  let submitting = $state(false)
  let error = $state<string | null>(null)

  $effect(() => {
    if (!open) {
      serverName = ''
      claimToken = ''
      error = null
      submitting = false
    }
  })

  async function onSubmit(e: Event) {
    e.preventDefault()
    const name = serverName.trim()
    const token = claimToken.trim()
    if (!name || !token || submitting) return

    submitting = true
    error = null
    try {
      await claimServer(token, name)
      open = false
      await onClaimed?.()
    } catch (err) {
      console.error('claim server failed', err)
      error =
        err instanceof Error && err.message
          ? err.message
          : 'Claim failed. Check the token in your server logs and try again.'
    } finally {
      submitting = false
    }
  }
</script>

<Dialog.Root bind:open>
  <Dialog.Content
    class="border-latte-surface1/60 bg-latte-mantle/95 sm:max-w-md
      dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/95"
  >
    <Dialog.Header>
      <Dialog.Title class="text-latte-text dark:text-macchiato-text">
        Claim a Server
      </Dialog.Title>
      <Dialog.Description class="text-latte-text/65 dark:text-macchiato-text/65">
        Enter the name you want and the claim token printed in your server logs
        when it first starts.
      </Dialog.Description>
    </Dialog.Header>

    <form class="flex flex-col gap-4" onsubmit={onSubmit}>
      <div class="flex flex-col gap-2">
        <Label for="claim-server-name">Server name</Label>
        <Input
          id="claim-server-name"
          bind:value={serverName}
          placeholder="name your server…"
          autocomplete="off"
          required
          disabled={submitting}
        />
      </div>
      <div class="flex flex-col gap-2">
        <Label for="claim-server-token">Claim token</Label>
        <Input
          id="claim-server-token"
          bind:value={claimToken}
          placeholder="paste claim token…"
          autocomplete="off"
          spellcheck={false}
          required
          disabled={submitting}
        />
      </div>

      {#if error}
        <div
          class="flex items-start gap-2 rounded-md border border-latte-red/35
            bg-latte-red/8 px-3 py-2 text-sm text-latte-red
            dark:border-macchiato-red/35 dark:bg-macchiato-red/10
            dark:text-macchiato-red"
          role="alert"
        >
          <AlertCircleIcon class="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
          <p class="min-w-0 leading-snug">{error}</p>
        </div>
      {/if}

      <Dialog.Footer class="gap-2 sm:justify-end">
        <Button
          type="button"
          variant="outline"
          disabled={submitting}
          onclick={() => (open = false)}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          class="bg-latte-green text-white hover:bg-latte-green/90
            dark:bg-macchiato-green dark:text-macchiato-base
            dark:hover:bg-macchiato-green/90"
          disabled={submitting || !serverName.trim() || !claimToken.trim()}
        >
          {submitting ? 'Claiming…' : 'Claim'}
        </Button>
      </Dialog.Footer>
    </form>
  </Dialog.Content>
</Dialog.Root>
